import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Link, Text, useTheme } from '@Sage/hoi-poi-ui';

import { formatDate } from 'lib/dates';
import { useWeb5Context } from 'lib/Web5Context';
import { getImplementationDeactivationInfo } from 'utils/implementationDeactivation';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ImplementationDeactivationBanner');

const ImplementationDeactivationBanner = ({
    enabled = true,
    expectedDeactivationDate,
    showInactiveMessage = false,
}) => {
    const { locale, t } = useWeb5Context();
    const theme = useTheme();
    const classes = useStyles();
    const { daysRemaining, isInactive, shouldShow } =
        getImplementationDeactivationInfo(expectedDeactivationDate);

    if (!enabled || !shouldShow) return null;

    const formattedDate = formatDate(expectedDeactivationDate, locale, 'P');
    const useInactiveMessage = showInactiveMessage && isInactive;
    const label = t(
        useInactiveMessage ? 'label_banner_impl_inactive' : 'label_banner_impl_deactivation',
    )
        .replace('%@', useInactiveMessage ? formattedDate : daysRemaining)
        .replace('%@', formattedDate);

    return (
        <div className={classes.root} role="status">
            <Icon name="warningOutline" color={theme.colors.primary.black} />
            <Text type="body" medium className={classes.content}>
                <span className={classes.label}>{label}</span>
                {!useInactiveMessage && (
                    <Link
                        type="body"
                        href={t('link_banner_impl_deactivation')}
                        target="_blank"
                        rel="noreferrer"
                        variation="primary"
                        className={classes.link}
                    >
                        {t('action_banner_impl_deactivation')}
                    </Link>
                )}
            </Text>
        </div>
    );
};

ImplementationDeactivationBanner.propTypes = {
    enabled: PropTypes.bool,
    expectedDeactivationDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    showInactiveMessage: PropTypes.bool,
};

export default memo(ImplementationDeactivationBanner);
